import React from 'react';
import ReactImageMagnify from 'react-image-magnify';
import Imgix from 'react-imgix';
import ReactLoading from 'react-loading';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import styled, { css } from 'styled-components';
import LeftArrow from '../../../../../common/svgs/LeftArrow';
import RightArrow from '../../../../../common/svgs/RightArrow';
import useMedia from '../../../../../hooks/useMedia';
import { getImgixUrl } from '@/utils/helper';
import { isQuickShip } from '@/common/QuickShip';
import QuickShipBadge from '@/components/customers/common/QuickShipBadge';

const Wrapper = styled.div`
  width: 100%;

  /* enables ability to copy url from zoom */
  .large-image {
    pointer-events: auto !important;
  }

  .slick-next {
    right: 35px;

    &:before {
      display: none;
    }
  }

  .slick-dots {
    bottom: 10px;
  }

  ${({ isThumbnail }) =>
    isThumbnail &&
    css`
      .slick-dots {
        bottom: -15px;
        li {
          width: 50px;
          height: 50px;

          &:hover {
            .thumbnail-image-container {
              border: 3px solid #6a99cc !important;
            }
          }
        }

        .slick-active {
          .thumbnail-image-container {
            border: 3px solid #2a5caf !important;
          }
        }
      }
    `}

  .magnify-container {
    width: 80% !important;
  }

  li.slick-active button:before {
    color: ${({ theme }) => theme.colors.french};
    opacity: 1;
    font-size: 8px;
  }

  .slick-prev {
    z-index: 1;
    left: 35px;
    &:before {
      display: none;
    }
  }
  .slick-arrow {
    top: 40%;

    svg {
      width: 13px;
      stroke: #7f7f7f;
      path {
        stroke-width: 3px;
      }
    }
  }
`;

function ImageSlider({
  productData,
  sliderImages,
  loading,
  hoverRefImage,
  sliderRef
}) {
  const isMobile =
    typeof window !== `undefined` ? useMedia('(max-width: 900px) ') : null;

  const isBigScreen =
    typeof window !== `undefined` ? useMedia('(min-width: 2400px) ') : null;

  const isThumbnail = true;

  const settings = {
    ...(!isMobile &&
      isThumbnail && {
        customPaging: function(i) {
          return (
            <SliderThumbnail>
              <div className="thumbnail-image-container">
                <Imgix
                  imgixParams={{ auto: 'compress,format' }}
                  sizes="50px"
                  src={getImgixUrl(sliderImages.map(x => x.src)[i])}
                  htmlAttributes={{
                    alt: 'product thumbnail image',
                    loading: 'lazy'
                  }}
                />
              </div>
            </SliderThumbnail>
          );
        }
      }),
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />
  };

  return (
    <Wrapper ref={hoverRefImage} isThumbnail={isThumbnail}>
      {isQuickShip(productData) && <QuickShipBadge width="8vw" />}

      <Slider {...settings} ref={sliderRef}>
        {sliderImages.map((image, i) => {
          return (
            <SliderItem key={i}>
              <div className="image-container">
                {loading ? (
                  <ReactLoading type="spin" color="#2a5caf" />
                ) : isBigScreen ||
                  (image.width != 1920 && image.width != 2133) ? (
                  <Imgix
                    imgixParams={{ auto: 'compress,format' }}
                    className="product-image"
                    sizes="(min-width: 1140px) 80vw,
                        100vw"
                    src={!loading ? getImgixUrl(image.src) : ''}
                    htmlAttributes={{
                      alt: image.name,
                      loading: 'lazy'
                    }}
                  />
                ) : (
                  <ReactImageMagnify
                    enlargedImagePosition="over"
                    isHintEnabled
                    hintTextTouch="Hold to Zoom"
                    {...{
                      smallImage: {
                        alt: image.name,
                        isFluidWidth: true,
                        // imageClassName="small-imageZZ",
                        src: isMobile ? image.src : image.src,
                        sizes: '(min-width: 1140px) 1040px, 100vw',
                        srcSet: getSrcSet(getImgixUrl(image.src)),
                        loading: 'lazy'
                      },
                      largeImage: {
                        src: `${getImgixUrl(image.src)}?auto=format`,
                        width: parseInt(image.width),
                        height:
                          image.height == 0 ? 1200 : parseInt(image.height),
                        loading: 'lazy'
                      },
                      lensStyle: {
                        backgroundColor: 'rgba(0,0,0,.6)'
                      },
                      imageClassName: 'small-image',
                      enlargedImageClassName: 'large-image',
                      enlargedImageContainerClassName: 'large-image-container',
                      className: 'magnify-container'
                    }}
                  />
                )}
              </div>
            </SliderItem>
          );
        })}
      </Slider>
    </Wrapper>
  );
}

export default ImageSlider;

const PrevArrow = props => {
  const { className, onClick } = props;
  return (
    <div className={className} onClick={onClick}>
      <LeftArrow />
    </div>
  );
};

const NextArrow = props => {
  const { className, onClick } = props;
  return (
    <div className={className} onClick={onClick}>
      <RightArrow />
    </div>
  );
};

const SliderThumbnail = styled.div`
  width: 100%;
  padding-bottom: 100%;
  position: relative;

  .thumbnail-image-container {
    align-items: center;
    border-radius: 50%;
    border: 3px solid transparent;
    display: flex;
    height: 100%;
    justify-content: center;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }
  img {
    padding: 2px;
    width: 80%;
    height: auto;
  }
`;

const SliderItem = styled.div`
  width: 100%;
  padding-bottom: 56.25%;
  position: relative;
  background: #f7f9fa;
  .image-container {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    top: 0;
    left: 0;
    height: 100%;
    position: absolute;
    img {
      height: 80%;
      width: auto;
    }
  }

  @media (max-width: 992px) {
    .image-container {
      img {
        max-height: 230px;
        height: 80%;
        width: auto;
      }
    }
  }
`;

const getSrcSet = url => {
  return `${url}?auto=format&w=100 100w,
   ${url}?auto=format&w=116 116w,
   ${url}?auto=format&w=134 134w,
   ${url}?auto=format&w=156 156w,
   ${url}?auto=format&w=182 182w,
   ${url}?auto=format&w=210 210w,
   ${url}?auto=format&w=244 244w,
   ${url}?auto=format&w=282 282w,
   ${url}?auto=format&w=328 328w,
   ${url}?auto=format&w=380 380w,
   ${url}?auto=format&w=442 442w,
   ${url}?auto=format&w=512 512w,
   ${url}?auto=format&w=594 594w,
   ${url}?auto=format&w=688 688w,
   ${url}?auto=format&w=798 798w,
   ${url}?auto=format&w=926 926w,
   ${url}?auto=format&w=1074 1074w,
   ${url}?auto=format&w=1246 1246w,
   ${url}?auto=format&w=1446 1446w,
   ${url}?auto=format&w=1678 1678w,
   ${url}?auto=format&w=1946 1946w,
   ${url}?auto=format&w=2258 2258w,
   ${url}?auto=format&w=2618 2618w,
   ${url}?auto=format&w=3038 3038w,
   ${url}?auto=format&w=3524 3524w,
   ${url}?auto=format&w=4088 4088w,
   ${url}?auto=format&w=4742 4742w,
   ${url}?auto=format&w=5500 5500w,
   ${url}?auto=format&w=6380 6380w,
   ${url}?auto=format&w=7400 7400w,
   ${url}?auto=format&w=8192 8192w`;
};
